import { ErrorHandler, Injectable, Injector, NgZone, inject } from '@angular/core';
import { HttpBackend, HttpClient, HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { NavigationEnd, Router } from '@angular/router';
import { Observable, tap } from 'rxjs';
import { filter } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { AuthService } from './auth.service';
import { DialogService } from './dialog.service';
import { NGSW_BYPASS } from './session-policy';

/**
 * User journeys (2026-09-29) — the browser's half of "what did this person do before it broke?" for the platform
 * owner. Records: screens opened, buttons pressed (their short label only), pop-ups answered (kind + tone + OK/Cancel,
 * never the title, message or typed text), browser errors, and requests that never reached the server (offline / timed out). The
 * server records data-changing, failed and slow requests itself and joins them through X-Request-Id / X-Client-Session.
 *
 * Privacy: no typed values, no search text, no query strings; long numbers and emails are masked before sending.
 * Steps are posted in small batches straight to the API (not through the app's interceptors → no spinner, no toasts,
 * no sign-out on a 401) every 10 s, and with keepalive when the tab is hidden or closed.
 */
type Step = {
  at: string; kind: 'page' | 'click' | 'dialog' | 'error' | 'network';
  path?: string; label?: string; detail?: string; method?: string; status?: number; durationMs?: number; requestId?: string;
};

@Injectable({ providedIn: 'root' })
export class JourneyService {
  /** One id per browser tab visit — the API stores it on its own records of this tab's requests too. */
  readonly sessionKey = JourneyService.newId(32);

  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly zone = inject(NgZone);
  private readonly dialogs = inject(DialogService);
  private readonly http = new HttpClient(inject(HttpBackend));   // bypasses every interceptor
  private readonly url = `${environment.apiUrl}/journey/events`;
  private buffer: Step[] = [];
  private started = false;
  private lastClick = { label: '', at: 0 };

  static newId(len = 24): string {
    const raw = (globalThis.crypto?.randomUUID?.() ?? `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`);
    return raw.replace(/[^a-zA-Z0-9]/g, '').slice(0, len);
  }

  /** Masks long numbers (phones, IDs) and emails, collapses spaces, bounds length. */
  static clean(text: string | null | undefined, max: number): string | undefined {
    if (!text) return undefined;
    let t = String(text)
      .replace(/[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}/g, '[email]')
      .replace(/\d[\d\s\-]{4,}\d/g, m => (m.replace(/\D/g, '').length >= 6 ? '[number]' : m))
      .replace(/\s+/g, ' ').trim();
    if (!t) return undefined;
    if (t.length > max) t = t.slice(0, max - 1) + '…';
    return t;
  }

  static pathOnly(url: string): string {
    return url.split(/[?#]/)[0] || '/';
  }

  /** "Warning pop-up", "Confirmation (danger)" … — what kind of pop-up was answered, with no text from it. */
  static dialogLabel(kind: string, tone: string): string {
    const what = kind === 'confirm' ? 'Confirmation' : kind === 'prompt' ? 'Question' : 'Message';
    return tone && tone !== 'info' ? `${what} (${tone})` : what;
  }

  start(): void {
    if (this.started) return;
    this.started = true;

    this.router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(e => this.add({ kind: 'page', path: JourneyService.pathOnly(e.urlAfterRedirects) }));

    // Kind + tone only (e.g. "Warning pop-up") — the title can carry a patient's name, so it is never recorded.
    this.dialogs.answered.subscribe(d =>
      this.add({ kind: 'dialog', path: this.here(), label: JourneyService.dialogLabel(d.kind, d.tone), detail: d.answer }));

    this.zone.runOutsideAngular(() => {
      // Buttons only (commands like "Collect payment", "Dispense") — not links or rows, which can carry patient names.
      document.addEventListener('click', ev => {
        const btn = (ev.target as Element | null)?.closest?.('button');
        if (!btn || btn.closest('app-dialog-host, [data-journey-ignore]')) return;
        const label = JourneyService.clean(btn.getAttribute('aria-label') || (btn as HTMLElement).innerText || btn.getAttribute('title'), 40);
        if (!label) return;
        const now = Date.now();
        if (label === this.lastClick.label && now - this.lastClick.at < 800) return;   // one step per double-click
        this.lastClick = { label, at: now };
        this.add({ kind: 'click', path: this.here(), label });
      }, true);

      // Errors outside Angular (third-party scripts, code run outside the zone). Angular's own errors never reach
      // window.onerror — zone.js hands them to JourneyErrorHandler — so nothing is counted twice.
      window.addEventListener('error', ev => this.recordError(ev.error ?? { name: 'Error', message: ev.message || 'Script failed to load' }));
      window.addEventListener('unhandledrejection', ev => this.recordError(ev.reason));

      setInterval(() => this.flush(), 10_000);
      document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') this.flush(true); });
      window.addEventListener('pagehide', () => this.flush(true));
    });
  }

  /** Called by JourneyErrorHandler for every uncaught error in the app. */
  recordError(err: unknown): void {
    const e = err as { name?: string; message?: string; stack?: string; rejection?: unknown } | null;
    const inner = (e?.rejection ?? e) as { name?: string; message?: string; stack?: string } | null;
    const name = inner?.name || 'Error';
    const msg = inner?.message || String(err ?? 'Unknown error');
    const frame = (inner?.stack || '').split('\n').map(s => s.trim()).find(s => s.startsWith('at ')) || '';
    this.add({ kind: 'error', path: this.here(), label: JourneyService.clean(`${name}: ${msg}`, 180), detail: JourneyService.clean(`${name}: ${msg} ${frame}`, 900) });
  }

  /** Called by JourneyInterceptor when a request never got an answer from the server. */
  recordNetworkFailure(method: string, url: string, requestId: string, durationMs: number, why: string): void {
    this.add({ kind: 'network', method, path: JourneyService.pathOnly(url.replace(environment.apiUrl, '/api')), requestId, durationMs, detail: why });
  }

  private here(): string { return JourneyService.pathOnly(this.router.url || location.pathname); }

  private add(step: Omit<Step, 'at'>): void {
    if (!this.auth.isLoggedIn) return;
    this.buffer.push({ at: new Date().toISOString(), ...step });
    if (this.buffer.length > 200) this.buffer.splice(0, this.buffer.length - 200);   // bounded while offline
    if (this.buffer.length >= 25) this.flush();
  }

  /** Sends what's buffered. keepalive = the tab is closing/hiding (fetch survives the page going away). */
  flush(keepalive = false): void {
    if (!this.buffer.length) return;
    const token = this.auth.token;
    if (!token) { this.buffer = []; return; }
    const events = this.buffer.splice(0, 50);
    const body = { sessionKey: this.sessionKey, events };
    if (keepalive && typeof fetch === 'function') {
      fetch(this.url, {
        method: 'POST', keepalive: true, credentials: 'include',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...NGSW_BYPASS },
        body: JSON.stringify(body),
      }).catch(() => { /* best effort */ });
      if (this.buffer.length) this.flush(true);
      return;
    }
    this.http.post(this.url, body, { headers: { Authorization: `Bearer ${token}`, ...NGSW_BYPASS }, withCredentials: true })
      .subscribe({ error: (err: HttpErrorResponse) => {
        // Offline / server busy → put them back for the next try; a 4xx (e.g. signed out) → drop them.
        if (err.status === 0 || err.status >= 500) this.buffer = [...events, ...this.buffer].slice(-200);
      } });
  }
}

/** Uncaught errors → the journey (then the normal console report). */
@Injectable()
export class JourneyErrorHandler extends ErrorHandler {
  // Looked up on first use: the ErrorHandler is built very early, before the router / auth it depends on.
  private readonly injector = inject(Injector);
  override handleError(error: unknown): void {
    try { this.injector.get(JourneyService).recordError(error); } catch { /* never let logging break error handling */ }
    super.handleError(error);
  }
}

/**
 * Tags every API request with X-Request-Id and X-Client-Session (so the owner's view can join the browser's and the
 * server's record of the same request) and reports requests that never got an answer. Registered FIRST, so it sees
 * the final outcome after the retry interceptor has given up.
 */
@Injectable()
export class JourneyInterceptor implements HttpInterceptor {
  private readonly journey = inject(JourneyService);

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!req.url.startsWith(environment.apiUrl)) return next.handle(req);
    const requestId = JourneyService.newId(24);
    const started = Date.now();
    const tagged = req.clone({ setHeaders: { 'X-Request-Id': requestId, 'X-Client-Session': this.journey.sessionKey } });
    return next.handle(tagged).pipe(tap({
      error: (err: unknown) => {
        const status = err instanceof HttpErrorResponse ? err.status : undefined;
        const isTimeout = (err as { name?: string })?.name === 'TimeoutError';
        if (status === 0 || isTimeout) {
          this.journey.recordNetworkFailure(req.method, req.url, requestId, Date.now() - started,
            isTimeout ? 'No answer in time (timed out)' : navigator.onLine ? 'No answer from the server' : 'Device offline');
        }
      },
    }));
  }
}
